import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Store, IndianRupee, Receipt, RefreshCw, Activity,
  TrendingUp, Clock, Users
} from 'lucide-react';
import { hqMonitoringAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const LiveSalesMonitoring = () => {
  const [liveData, setLiveData] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [liveRes, hourlyRes] = await Promise.all([
        hqMonitoringAPI.getLiveSales(),
        hqMonitoringAPI.getHourlySales()
      ]);
      setLiveData(liveRes.data);
      setHourlyData(hourlyRes.data.hourly_data || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching live data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const getStoreStatusColor = (isLive) => {
    return isLive ? 'bg-emerald-500' : 'bg-gray-400';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate max sales for bar chart
  const maxHourlySales = Math.max(...hourlyData.map(h => h.sales), 1);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading live data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="live-sales-monitoring">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="w-7 h-7 text-blue-600" />
            Live Sales Monitoring
          </h1>
          <p className="text-gray-500 mt-1">Real-time sales data across all stores</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Last updated: {formatDate(lastRefresh.toISOString())}
          </div>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Stores</p>
                <p className="text-3xl font-bold mt-1">{liveData?.summary?.total_stores || 0}</p>
                <p className="text-blue-200 text-xs mt-1">
                  {liveData?.summary?.active_stores || 0} active now
                </p>
              </div>
              <Store className="w-12 h-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Today's Sales</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(liveData?.summary?.total_sales_today || 0)}
                </p>
                <p className="text-emerald-200 text-xs mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Live updating
                </p>
              </div>
              <IndianRupee className="w-12 h-12 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Today's Bills</p>
                <p className="text-3xl font-bold mt-1">{liveData?.summary?.total_bills_today || 0}</p>
                <p className="text-purple-200 text-xs mt-1">Transactions</p>
              </div>
              <Receipt className="w-12 h-12 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0 shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Avg Bill Value</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(
                    liveData?.summary?.total_bills_today > 0
                      ? liveData?.summary?.total_sales_today / liveData?.summary?.total_bills_today
                      : 0
                  )}
                </p>
                <p className="text-amber-200 text-xs mt-1">Per transaction</p>
              </div>
              <TrendingUp className="w-12 h-12 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Sales Chart */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Hourly Sales Trend (Today)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-32 mt-4">
            {hourlyData.map((hour, idx) => {
              const height = maxHourlySales > 0 ? (hour.sales / maxHourlySales) * 100 : 0;
              const isCurrentHour = new Date().getHours() === hour.hour;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className={`w-full rounded-t transition-all ${
                      isCurrentHour 
                        ? 'bg-emerald-500' 
                        : hour.sales > 0 ? 'bg-blue-400' : 'bg-gray-200'
                    }`}
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${hour.label}: ${formatCurrency(hour.sales)} (${hour.bills} bills)`}
                  />
                  {idx % 3 === 0 && (
                    <span className="text-[10px] text-gray-500">{hour.label}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>12 AM</span>
            <span>6 AM</span>
            <span>12 PM</span>
            <span>6 PM</span>
            <span>11 PM</span>
          </div>
        </CardContent>
      </Card>

      {/* Store Cards Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Store-wise Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {liveData?.stores?.map((store) => (
            <Card 
              key={store.store_id} 
              className={`relative overflow-hidden transition-all hover:shadow-lg ${
                store.is_live ? 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50' : 'border-gray-200'
              }`}
            >
              {/* Live indicator */}
              <div className={`absolute top-0 left-0 right-0 h-1 ${getStoreStatusColor(store.is_live)}`} />
              
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">{store.store_name}</h3>
                    <p className="text-xs text-gray-500">{store.store_code} • {store.city}</p>
                  </div>
                  <Badge 
                    variant={store.is_live ? "default" : "secondary"}
                    className={store.is_live ? "bg-emerald-500" : ""}
                  >
                    {store.is_live ? (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        LIVE
                      </span>
                    ) : 'Closed'}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Today's Sales</span>
                    <span className="font-bold text-emerald-600">
                      {formatCurrency(store.today_sales)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Bills</span>
                    <span className="font-semibold text-gray-700">{store.today_bills}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Avg Value</span>
                    <span className="text-gray-600">{formatCurrency(store.avg_bill_value)}</span>
                  </div>
                </div>

                {store.is_live && store.cashier_name && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Cashier: <span className="font-medium text-gray-700">{store.cashier_name}</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* No stores message */}
      {(!liveData?.stores || liveData.stores.length === 0) && (
        <Card className="p-8 text-center">
          <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No stores found</p>
        </Card>
      )}
    </div>
  );
};

export default LiveSalesMonitoring;
